// The `400` body `_persist_dashboard_definition` returns when `validate_sections_for_save`
// refuses a widget, normalised into `SectionsValidationError[]`.
//
// WHY THIS IS TOLERANT RATHER THAN EXACT: the backend is not checked out in this workspace, so
// the envelope key carrying the per-widget list is unverified — `errors`, `data` and a bare
// top-level array are all plausible for this codebase's error shapes. Every candidate is tried
// and anything unrecognised degrades to `[]`, which the funnel reports as "invalid, no detail"
// rather than mislabelling it as a generic failure. Narrow this to the one real key when
// BE-09-07 publishes the schema.
//
// This matters more than the usual error-parsing helper: replaying `validate_sections_for_save`
// over production showed **72 of 261 live dashboards already reject on ANY save** (unknown
// `multi_pie` chart type, `line` with no time dimension, `compare_column` with no compare_type).
// For those, a save fails on a payload the user never authored, so a message naming the widget
// is the difference between an actionable report and an unexplained dead end.

import type { SectionsValidationError } from '../models/api.types'

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value))
    return value.filter((entry): entry is string => typeof entry === 'string')
  return typeof value === 'string' ? [value] : []
}

/** One entry is a widget verdict only if it actually carries messages — otherwise it is noise. */
function toEntry(value: unknown): SectionsValidationError | null {
  const record = asRecord(value)
  if (!record) return null

  const errors = toStringArray(record.errors ?? record.messages ?? record.detail)
  if (errors.length === 0) return null

  const type = record.type === 'chart' || record.type === 'table' ? record.type : 'chart'
  const entry: SectionsValidationError = {
    widget_id: typeof record.widget_id === 'string' ? record.widget_id : null,
    type,
    errors,
  }
  if (typeof record.chart_type === 'string') entry.chart_type = record.chart_type
  if (typeof record.table_type === 'string') entry.table_type = record.table_type
  return entry
}

function fromList(value: unknown): SectionsValidationError[] {
  if (!Array.isArray(value)) return []
  return value.map(toEntry).filter((entry): entry is SectionsValidationError => entry !== null)
}

/**
 * Pull the per-widget verdicts out of a 400 payload. Returns `[]` when the body carries none —
 * a caller must treat that as "rejected without detail", never as "no problem".
 */
export function parseSectionsValidationErrors(payload: unknown): SectionsValidationError[] {
  const direct = fromList(payload)
  if (direct.length > 0) return direct

  const body = asRecord(payload)
  if (!body) return []

  for (const key of ['errors', 'data', 'detail', 'sections'] as const) {
    const found = fromList(body[key])
    if (found.length > 0) return found
  }

  // `{ error: { fields: {...} } }` / `{ error: [...] }` — the envelope ApiError already mines.
  const nested = asRecord(body.error)
  if (nested) {
    for (const key of ['errors', 'data', 'detail'] as const) {
      const found = fromList(nested[key])
      if (found.length > 0) return found
    }
  }
  return fromList(body.error)
}
